import { Input } from "antd"
import type { InputProps } from "antd/es/input"
import React from "react"

type CountryCode = "US"

interface CountryConfig {
  code: string
  phoneMask: (digits: string) => string
  maxDigits: number
  placeholder: string
}

const countryConfigs: Record<CountryCode, CountryConfig> = {
  US: {
    code: "1",
    maxDigits: 11,
    placeholder: "+1 (XXX) XXX-XXXX",
    phoneMask: (digits) => {
      if (digits.length === 0) return ""
      if (digits.length <= 1) return `+${digits}`
      if (digits.length <= 4) return `+${digits[0]} (${digits.slice(1)}`
      if (digits.length <= 7)
        return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4)}`
      return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
    }
  }
}

/**
 * Display formatting for a stored phone: renders a US 10-digit number — bare,
 * 11 digits with the country code, or E.164 — as `+1 (XXX) XXX-XXXX`. Anything
 * that is not a US 10-digit number is returned unchanged, so an international or
 * partial value is never mangled.
 */
export const formatUsPhone = (value: string | null | undefined): string => {
  if (!value) return ""
  const digits = value.replace(/\D/g, "")
  const national =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits
  if (national.length !== 10) return value
  return countryConfigs.US.phoneMask(`1${national}`)
}

interface PhoneInputProps extends Omit<InputProps, "onChange" | "value"> {
  value?: string | null
  country?: CountryCode
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export const PhoneInput = ({
  value,
  onChange,
  country = "US",
  placeholder,
  ...rest
}: PhoneInputProps) => {
  const config = countryConfigs[country]

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    let digits = raw.replace(/\D/g, "").slice(0, config.maxDigits)

    // Country code auto-substitution
    if (digits.length > 0 && !digits.startsWith(config.code)) {
      // If the user starts typing without a code, add it
      if (digits.length <= config.maxDigits - config.code.length) {
        digits = config.code + digits
      }
    }

    digits = digits.slice(0, config.maxDigits)

    const itu = digits.length > 0 ? `+${digits}` : ""

    onChange?.({
      ...e,
      target: { ...e.target, value: itu },
      currentTarget: { ...e.currentTarget, value: itu }
    } as React.ChangeEvent<HTMLInputElement>)
  }

  // EAD-1290: a stored/linked value can arrive as a bare national number
  // (10 digits, no country code) — a linked customer's `phone` from the
  // search API is exactly this shape. `phoneMask` always reads its first
  // digit as the country code, so masking that bare number directly shifted
  // every group by one digit (`4155550216` rendered as "+4 (155) 550-216"
  // instead of "+1 (415) 555-0216"). Only a *complete* national number is
  // normalized this way — an in-progress typed value (any other length)
  // renders exactly as it always has, so live masking-while-typing is
  // unaffected; `handleChange` above already does the equivalent
  // normalization for what the user types.
  const rawDigits = value?.replace(/\D/g, "") ?? ""
  const nationalLength = config.maxDigits - config.code.length
  const digits =
    rawDigits.length === nationalLength && !rawDigits.startsWith(config.code)
      ? config.code + rawDigits
      : rawDigits
  const formattedValue = config.phoneMask(digits)

  return (
    <Input
      value={formattedValue}
      onChange={handleChange}
      placeholder={placeholder ?? config.placeholder}
      {...rest}
    />
  )
}
